
import React, { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AICopilotContextType, AICopilotProviderProps, CopilotAttachment, AICopilotMessageRole, CopilotMessage } from './types';
import { useAICopilotCore } from './useAICopilotCore';
import { trackError } from '@/utils/error/errorTracker';

const AICopilotContext = createContext<AICopilotContextType | undefined>(undefined);

export function AICopilotProvider({ children }: AICopilotProviderProps) {
  const copilotState = useAICopilotCore();
  const location = useLocation();
  
  // Check for and auto-submit initial prompt without auto-opening on page load
  useEffect(() => {
    const initialPrompt = localStorage.getItem('initialAICopilotPrompt');
    if (initialPrompt) {
      // Don't automatically open the copilot - user must click to open
      // Store the prompt to be used when they do open
      localStorage.setItem('pendingAICopilotPrompt', initialPrompt);
      localStorage.removeItem('initialAICopilotPrompt');
    }
  }, []);

  // Log state for debugging
  useEffect(() => {
    console.log('AICopilot Context State:', {
      isOpen: copilotState.isOpen,
      currentConversation: copilotState.currentConversation?.id,
      isLoading: copilotState.isLoading,
      conversationsCount: copilotState.conversations?.length || 0,
      hash: window.location.hash,
      aiInteractionFlag: sessionStorage.getItem('ai-interaction-active')
    });
  }, [
    copilotState.isOpen, 
    copilotState.currentConversation?.id, 
    copilotState.isLoading,
    copilotState.conversations?.length
  ]);

  // Implement the necessary properties to match AICopilotContextType
  const fullCopilotState: AICopilotContextType = {
    isOpen: copilotState.isOpen,
    messages: copilotState.currentConversation?.messages || [],
    status: copilotState.isLoading ? 'thinking' : 'idle',
    inputValue: '',
    toggleCopilot: copilotState.toggleCopilot,
    openCopilot: copilotState.openCopilot,
    closeCopilot: copilotState.closeCopilot,
    // Fixed type compatibility issue by correctly handling attachments
    sendMessage: async (content, attachments = []) => {
      try {
        // Convert CopilotAttachment[] to File[] if needed
        if (attachments && attachments.length > 0) {
          // Type guard to check if attachments are already File objects
          const isFileArray = (arr: any[]): arr is File[] => 
            arr.length === 0 || arr[0] instanceof File;
          
          if (isFileArray(attachments)) {
            await copilotState.sendMessage(content, attachments);
          } else {
            // Handle case where we need to convert from CopilotAttachment to File
            console.warn('Attachment format conversion not implemented');
            await copilotState.sendMessage(content);
          }
        } else {
          await copilotState.sendMessage(content);
        }
        return {} as CopilotMessage;
      } catch (error) {
        console.error('Error sending message:', error);
        return {} as CopilotMessage;
      }
    },
    addMessage: (content, role = 'user') => {
      const newMessage: CopilotMessage = {
        id: `${role}-${Date.now()}`,
        role: role as AICopilotMessageRole,
        content: content,
        timestamp: new Date().toISOString()
      };
      return newMessage;
    },
    clearMessages: () => {
      if (copilotState.clearConversation) {
        copilotState.clearConversation();
      }
    },
    setInputValue: () => {
      // Implementation is handled in input component
      console.warn("setInputValue called from context - not implemented at this level");
    },
    cancelResponse: copilotState.cancelResponse || (() => {
      console.warn("cancelResponse not available");
    }),
    isLoading: copilotState.isLoading,
    conversations: copilotState.conversations || [],
    currentConversation: copilotState.currentConversation,
    switchConversation: copilotState.switchConversation || (() => {
      console.warn("switchConversation not available");
    }),
    createNewConversation: copilotState.createNewConversation || (() => {
      console.warn("createNewConversation not available");
      return null;
    }),
    deleteConversation: copilotState.deleteConversation || (() => {
      console.warn("deleteConversation not available");
    }),
    clearConversation: copilotState.clearConversation || (() => {
      console.warn("clearConversation not available");
    }),
  };

  return (
    <AICopilotContext.Provider value={fullCopilotState}>
      {children}
    </AICopilotContext.Provider>
  );
}

export function useAICopilot() {
  const context = useContext(AICopilotContext);
  
  if (context === undefined) {
    const error = new Error('useAICopilot must be used within an AICopilotProvider');
    console.error(error);
    // Track the error but don't throw it to prevent app crashes
    trackError(error, 'useAICopilot', {
      url: window.location.href,
      hash: window.location.hash
    });
    
    // Return a safe fallback object with no-op functions
    const fallbackState: AICopilotContextType = {
      isOpen: false,
      messages: [],
      status: 'idle',
      inputValue: '',
      toggleCopilot: () => console.warn("AI Copilot not available"),
      openCopilot: () => console.warn("AI Copilot not available"),
      closeCopilot: () => console.warn("AI Copilot not available"),
      sendMessage: async () => {
        console.warn("AI Copilot not available");
        return undefined;
      },
      addMessage: () => {
        console.warn("AI Copilot not available");
        return {
          id: '',
          content: '',
          role: 'assistant',
          timestamp: new Date().toISOString()
        };
      },
      clearMessages: () => console.warn("AI Copilot not available"),
      setInputValue: () => console.warn("AI Copilot not available"),
      cancelResponse: () => console.warn("AI Copilot not available"),
      isLoading: false,
      conversations: [],
      currentConversation: null,
      switchConversation: () => console.warn("AI Copilot not available"),
      createNewConversation: () => {
        console.warn("AI Copilot not available");
        return null;
      },
      deleteConversation: () => console.warn("AI Copilot not available"),
      clearConversation: () => console.warn("AI Copilot not available"),
    };
    
    return fallbackState;
  }
  
  return context;
}
